// StartScreen — extrahiert aus
// app/src/components/editor/Viewer/StartScreen/StartScreen.tsx
// (Plan A Mapping-Audit 2026-05-22).
//
// Abstrahiert app/-Coupling:
//   - `viewerFont` (SofiaSansSemiCondensed) inline-kopiert
//   - `OptimizedCloudinaryImage` durch native <img> ersetzt
//   - `SIZES`, `TEXT_SHADOW` aus packages/viewer/constants

import type { CustomFonts, PostJson } from "@scrolly/post-types";
import { SIZES, TEXT_SHADOW } from "./constants";
import { StoryLoaderStartButton } from "./StoryLoaderStartButton";

const VIEWER_FONT_FALLBACK = "SofiaSansSemiCondensed";

const STAGGER = 0.4;

const fadeIn = (delay: number): React.CSSProperties => ({
  opacity: 0,
  animationName: "startScreenFadeIn",
  animationDelay: `${delay}s`,
  animationFillMode: "forwards",
  animationDuration: "1s",
});

interface Props {
  post: PostJson;
  progress: number;
  onStartClick: () => void;
  fontFamilies: CustomFonts;
  height: number;
  /**
   * OG-Image render mode (Plan-1.9 STOPP-D Hotfix CP2 refinement).
   * Versteckt nur das CTA-Element (StoryLoaderStartButton), Cover-Inhalt
   * (Title, Description, Author) bleibt sichtbar.
   */
  isOgMode?: boolean;
}

export function StartScreen({
  post,
  progress,
  onStartClick,
  fontFamilies,
  height,
  isOgMode = false,
}: Props) {
  const metadata = post.data?.metadata;
  const titleImage = metadata?.titleImage;
  const titleImageUrl =
    titleImage !== undefined ? titleImage.ppiUrl || titleImage.url : undefined;

  const authorsString =
    post.data.authors && post.data.authors.length > 0
      ? `By ${post.data.authors
          .map((author) => `${author.firstName} ${author.lastName}`)
          .join(" & ")}`
      : null;

  const onStartButtonClick = () => {
    onStartClick();
  };

  return (
    <>
      {/* Inject the @keyframes rule for fadeIn */}
      <style>{`@keyframes startScreenFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>

      <div
        className="absolute left-0 top-0 z-[1002] flex w-full flex-col items-center justify-center overflow-hidden"
        style={{ height: `${height}px`, transition: "opacity 0.8s" }}
      >
        {!metadata?.hideIntroOverlay && (
          <div
            className="absolute inset-0 z-[1]"
            style={{ background: "rgba(0, 0, 0, 0.6)", color: "white" }}
          />
        )}
        <div
          className="absolute z-[1] mx-auto flex flex-col items-center px-4 pt-4"
          style={{
            width: "100%",
            maxWidth: `${SIZES.storyHeroTitleMaxWidth}px`,
            color: "white",
          }}
        >
          {/* Heading */}
          <div
            className="mb-8 w-full text-center"
            style={{
              font: `bold 42px/1.1 '${fontFamilies.headlineFontFamily || VIEWER_FONT_FALLBACK}'`,
              textShadow: TEXT_SHADOW,
              ...fadeIn(STAGGER * 0),
            }}
          >
            {titleImage && titleImageUrl ? (
              <img
                src={titleImageUrl}
                sizes={`(max-width: ${SIZES.storyHeroTitleMaxWidth}px) 100vw, ${SIZES.storyHeroTitleMaxWidth}px`}
                alt={metadata.title ?? ""}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            ) : (
              metadata?.title
            )}
          </div>

          {/* Description */}
          <p
            className="mb-6 text-center"
            style={{
              font: `18px/1.4 '${fontFamilies.textFontFamily || VIEWER_FONT_FALLBACK}'`,
              textShadow: TEXT_SHADOW,
              ...fadeIn(STAGGER * 1),
            }}
          >
            {post.data.metadata?.subtitle}
          </p>

          {/* Start Button — versteckt im OG-Mode */}
          {!isOgMode && (
            <StoryLoaderStartButton
              progress={progress}
              asset={metadata?.startButtonImage}
              onClick={onStartButtonClick}
            />
          )}

          {/* Author */}
          {authorsString && (
            <h6
              className="mb-[30px] uppercase"
              style={{
                font: `bold 12px ${VIEWER_FONT_FALLBACK}`,
                textShadow: TEXT_SHADOW,
                ...fadeIn(STAGGER * 2),
              }}
            >
              {authorsString}
            </h6>
          )}
        </div>
      </div>
    </>
  );
}

export default StartScreen;
